import * as React from "react";
import { SWRConfig } from "swr";
import { Button } from "../../button";
import { ApiError, apiCall, useApi, useCursorList } from "../../api/client";
import { universalVerification } from "../../api/contracts/universal-verification";
import {
  verificationEvidence as api,
  sourceIntentCompileBody,
  sourceIntentReviewBody,
  sourceIntentIntakeBody,
  type SourceIntentScenario,
  type SourceIntentReviewBody,
  type SourceIntentGrantedSource,
} from "../../api/contracts/verification-evidence";
import {
  bindSourceReviewAcceptanceSuccess,
  prepareSourceReviewAcceptance,
  sourceReviewMatchesSaved,
  type PendingSourceReviewAcceptance,
  type SourceReviewAcceptanceSuccess,
} from "./source-review-acceptance-state";

type Choice = SourceIntentReviewBody["selections"][number];
type ReviewMode = "measurements" | "host-checks";
type SavedMode = { reviewDigest: string; mode: ReviewMode };

export function sourceReviewModeForSaved(saved: {
  selections: readonly unknown[];
  checks: readonly { kind?: string }[];
}): ReviewMode | null {
  if (saved.selections.length > 0) return "measurements";
  // This identifies the displayed binding kind, never authority/completeness.
  return saved.checks.some((check) =>
    ["artifact_present", "resource_bounds", "json_equals", "exit_code"].includes(check.kind ?? ""),
  )
    ? "host-checks"
    : null;
}

export function sourceReviewDraft(
  input: Omit<SourceIntentReviewBody, "idempotencyKey" | "bindingKind"> & {
    mode: ReviewMode;
  },
): Omit<SourceIntentReviewBody, "idempotencyKey"> {
  const { mode, ...body } = input;
  return {
    ...body,
    selections: mode === "host-checks" ? [] : body.selections,
    ...(mode === "host-checks" ? { bindingKind: "host-checks" as const } : {}),
  };
}

export function sourceReviewModeMatchesCurrent(
  saved:
    | {
        reviewDigest: string;
        compilationId: string;
        compilationDigest: string;
        selections: readonly unknown[];
        checks: readonly { kind?: string }[];
      }
    | undefined,
  compiled: { compilationId: string; compilationDigest: string } | undefined,
  mode: ReviewMode,
  known: SavedMode | null,
): boolean {
  if (
    !saved ||
    !compiled ||
    saved.compilationId !== compiled.compilationId ||
    saved.compilationDigest !== compiled.compilationDigest
  )
    return false;
  const savedMode =
    known?.reviewDigest === saved.reviewDigest ? known.mode : sourceReviewModeForSaved(saved);
  return savedMode === mode;
}
const readOptions = {
  refreshInterval: 15_000,
  revalidateOnFocus: true,
  shouldRetryOnError: false,
  dedupingInterval: 0,
};
const fieldClass = "w-full min-w-0 rounded border bg-background p-2 type-body";

/** Pre-acceptance review only. A private memory cache keeps source quotations out
 * of the shell's persisted SWR cache. All writes recheck authority on the server. */
export interface SourceCompilerReviewProps {
  renderCoverage?: (selection: {
    scenario: SourceIntentScenario;
    compilationId: string;
    expectedCompilationDigest: string;
    onApproved?: () => void;
  }) => React.ReactNode;
}
export function SourceCompilerReview({ renderCoverage }: SourceCompilerReviewProps = {}) {
  const cache = React.useMemo(() => new Map(), []);
  return (
    <SWRConfig value={{ provider: () => cache }}>
      <SessionReview renderCoverage={renderCoverage} />
    </SWRConfig>
  );
}

function SessionReview({ renderCoverage }: SourceCompilerReviewProps) {
  const session = useApi(api.sourceIntentSession, {}, readOptions);
  return (
    <section
      aria-labelledby="source-compiler-title"
      className="my-6 grid min-w-0 grid-cols-1 gap-3 rounded-lg border p-4 [overflow-wrap:anywhere] [&_button]:max-w-full [&_button]:whitespace-normal"
      data-source-compiler-review
    >
      <h2 id="source-compiler-title" className="type-title font-medium">
        Review source-derived checks
      </h2>
      <p className="type-body text-muted-foreground">
        Compile a saved, authorized source intake and review its measurement choices. This does not
        accept a contract or verify an outcome.
      </p>
      {session.error ? (
        <p role="alert">
          Sign in with an active workspace membership and one linked human actor.{" "}
          {session.error.message}
        </p>
      ) : !session.data ? (
        <p role="status">Checking source-review access…</p>
      ) : !session.data.compilerEnabled ? (
        <p role="status">Source compilation is not enabled on this host.</p>
      ) : (
        <AuthorizedReview
          key={`${session.data.tenantId}:${session.data.actorId}`}
          renderCoverage={renderCoverage}
          tenantId={session.data.tenantId}
          actorId={session.data.actorId}
          canReview={session.data.canReview && !session.isValidating}
        />
      )}
    </section>
  );
}

function AuthorizedReview(props: { tenantId: string; actorId: string; canReview: boolean } & SourceCompilerReviewProps) {
  const cache = React.useMemo(() => new Map(), []);
  return (
    <SWRConfig value={{ provider: () => cache }}>
      <ReviewEditor {...props} />
    </SWRConfig>
  );
}

function ReviewEditor({
  tenantId,
  actorId,
  canReview,
  renderCoverage,
}: {
  tenantId: string;
  actorId: string;
  canReview: boolean;
} & SourceCompilerReviewProps) {
  const [scenario, setScenario] = React.useState<SourceIntentScenario>(
    "workflow-template-observed-occurrence",
  );
  const [intakeId, setIntakeId] = React.useState("");
  const [statusIntakeId, setStatusIntakeId] = React.useState("");
  const [sourceChoices, setSourceChoices] = React.useState<
    Record<string, SourceIntentGrantedSource>
  >({});
  const [lookupId, setLookupId] = React.useState("");
  const [lookupKind, setLookupKind] = React.useState<"compilation" | "review">("compilation");
  const [acceptWorkItemId, setAcceptWorkItemId] = React.useState("");
  const [acceptVersion, setAcceptVersion] = React.useState("1");
  const [acceptWorkerActorId, setAcceptWorkerActorId] = React.useState("excellent:worker");
  const [compilationId, setCompilationId] = React.useState<string | null>(null);
  const [reviewId, setReviewId] = React.useState<string | null>(null);
  const [validatedCompilationId, setValidatedCompilationId] = React.useState<string | null>(null);
  const [validatedReviewId, setValidatedReviewId] = React.useState<string | null>(null);
  const [choices, setChoices] = React.useState<Record<string, Choice>>({});
  const [reviewMode, setReviewMode] = React.useState<ReviewMode>("measurements");
  const [knownSavedMode, setKnownSavedMode] = React.useState<SavedMode | null>(null);
  const lastCompilation = React.useRef<string | null>(null);
  const [contextDecisions, setContextDecisions] = React.useState<
    Record<string, "context" | "requirement">
  >({});
  const [busy, setBusy] = React.useState(false);
  const [compilingRequestId, setCompilingRequestId] = React.useState<string | null>(null);
  const [cancelling, setCancelling] = React.useState(false);
  const activeCompileRequest = React.useRef<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [now, setNow] = React.useState(Date.now());
  const generation = React.useRef(0);
  const pendingSave = React.useRef<{ fingerprint: string; body: SourceIntentReviewBody } | null>(
    null,
  );
  const pendingAcceptance = React.useRef<PendingSourceReviewAcceptance | null>(null);
  const [acceptedSourceReview, setAcceptedSourceReview] =
    React.useState<SourceReviewAcceptanceSuccess | null>(null);
  React.useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(timer);
      generation.current++;
    };
  }, []);
  const query = { env: "real" as const, actorId, scenario };
  React.useEffect(() => {
    const timer = setTimeout(() => setStatusIntakeId(intakeId.trim()), 300);
    return () => clearTimeout(timer);
  }, [intakeId]);
  const requestStatus = useApi(
    api.sourceIntentCompileStatus,
    statusIntakeId ? { query: { ...query, intakeId: statusIntakeId } } : null,
    { ...readOptions, refreshInterval: 3000, keepPreviousData: false },
  );
  const status =
    !requestStatus.error &&
    statusIntakeId === intakeId.trim() &&
    requestStatus.data?.intakeId === statusIntakeId
      ? requestStatus.data
      : undefined;
  const requestRunning = status?.state === "running";
  const mayStartCompile = Boolean(
    status && ["none", "failed", "cancelled", "interrupted", "expired"].includes(status.state),
  );
  const library = useCursorList(api.sourceIntentLibrary, { query, limit: 20, swr: readOptions });
  const sources = useCursorList(api.sourceIntentSources, { query, limit: 20, swr: readOptions });
  const selectedSources = Object.values(sourceChoices);
  const staleSourceSelection = selectedSources.some((selected) =>
    sources.rows.some(
      (current) =>
        current.source.id === selected.source.id &&
        (current.grantRevision !== selected.grantRevision ||
          current.source.revoked ||
          current.source.text === null),
    ),
  );
  const canCreateIntake =
    selectedSources.some(
      (row) =>
        row.source.kind === "trusted_workflow_template" && row.source.permissionTier === "content",
    ) &&
    selectedSources.some(
      (row) =>
        row.source.kind === "observed_occurrence" && row.source.permissionTier === "verification",
    );
  const compilation = useApi(
    api.sourceIntentCompilationGet,
    compilationId ? { query: { ...query, compilationId } } : null,
    {
      ...readOptions,
      onSuccess: (data) => setValidatedCompilationId(data.compilationId),
      onError: () => setValidatedCompilationId(null),
    },
  );
  const review = useApi(
    api.sourceIntentReviewGet,
    reviewId ? { query: { ...query, reviewId } } : null,
    {
      ...readOptions,
      onSuccess: (data) => setValidatedReviewId(data.reviewId),
      onError: () => setValidatedReviewId(null),
    },
  );
  const readError = compilation.error ?? review.error;
  const compiled =
    !readError && validatedCompilationId === compilationId ? compilation.data : undefined;
  const saved = !readError && validatedReviewId === reviewId ? review.data : undefined;
  const expired = Boolean(compiled && Date.parse(compiled.expiresAt) <= now);
  const current = !reviewId || Boolean(saved?.current);
  const localReviewSnapshot = {
    selections: reviewMode === "host-checks" ? [] : Object.values(choices),
    contextDecisions: Object.entries(contextDecisions).map(([clauseId, disposition]) => ({
      clauseId,
      disposition,
    })),
  };
  const savedReviewStillSelected =
    sourceReviewModeMatchesCurrent(saved, compiled, reviewMode, knownSavedMode) &&
    sourceReviewMatchesSaved(saved, localReviewSnapshot);
  const acceptedSavedReview =
    acceptedSourceReview &&
    saved &&
    acceptedSourceReview.fingerprint === pendingAcceptance.current?.fingerprint &&
    acceptedSourceReview.reviewId === saved.reviewId &&
    acceptedSourceReview.reviewDigest === saved.reviewDigest &&
    acceptedSourceReview.compilationDigest === saved.compilationDigest
      ? acceptedSourceReview
      : null;
  const locked =
    busy ||
    !canReview ||
    Boolean(readError) ||
    expired ||
    !current ||
    compilation.isValidating ||
    review.isValidating;

  React.useEffect(() => {
    if (saved) {
      setCompilationId(saved.compilationId);
      const mode =
        knownSavedMode?.reviewDigest === saved.reviewDigest
          ? knownSavedMode.mode
          : sourceReviewModeForSaved(saved);
      setReviewMode(mode ?? "measurements");
      setChoices(
        Object.fromEntries(saved.selections.map((choice) => [choice.predicateId, choice])),
      );
      setContextDecisions(
        Object.fromEntries(
          (saved.contextDecisions ?? []).map((decision) => [
            decision.clauseId,
            decision.disposition,
          ]),
        ),
      );
      pendingSave.current = null;
      pendingAcceptance.current = null;
      setAcceptedSourceReview(null);
    }
  }, [saved?.reviewDigest]);
  React.useEffect(() => {
    if (!compiled) return;
    const identity = `${compiled.compilationId}:${compiled.compilationDigest}`;
    if (lastCompilation.current && lastCompilation.current !== identity) {
      pendingSave.current = null;
      pendingAcceptance.current = null;
      setAcceptedSourceReview(null);
      if (
        saved?.compilationId !== compiled.compilationId ||
        saved.compilationDigest !== compiled.compilationDigest
      ) {
        setReviewId(null);
        setKnownSavedMode(null);
        setReviewMode("measurements");
        setChoices({});
        setContextDecisions({});
      }
    }
    lastCompilation.current = identity;
  }, [compiled?.compilationId, compiled?.compilationDigest]);
  React.useEffect(() => {
    if (readError) {
      setReviewMode("measurements");
      setKnownSavedMode(null);
      setChoices({});
      setContextDecisions({});
      pendingSave.current = null;
      pendingAcceptance.current = null;
      setAcceptedSourceReview(null);
    }
  }, [readError]);

  function reset() {
    generation.current++;
    setCompilationId(null);
    setReviewId(null);
    setChoices({});
    setReviewMode("measurements");
    setKnownSavedMode(null);
    lastCompilation.current = null;
    setContextDecisions({});
    setValidatedCompilationId(null);
    setValidatedReviewId(null);
    setError(null);
    setNotice(null);
    pendingSave.current = null;
    pendingAcceptance.current = null;
    setAcceptedSourceReview(null);
  }
  React.useEffect(() => {
    setSourceChoices({});
  }, [scenario, sources.error]);
  async function perform(action: () => Promise<void>) {
    if (busy) return;
    const version = generation.current;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
    } catch (failure) {
      if (version === generation.current) {
        const message = failure instanceof Error ? failure.message : String(failure);
        setError(
          message.includes("source-compiler-model-identity-")
            ? "Local model identity could not be confirmed. Ask the host operator to restore the configured baseline runtime, then retry. No compilation was saved."
            : message.includes("source-compiler-recovery-unavailable")
              ? "Compilation failed and its final state could not be recorded. Ask the host operator to restore storage, then refresh request status before retrying. Do not assume the request stopped or a result was saved."
              : /source-compiler-(request-capacity|proposal-capacity)/.test(message)
                ? "Compilation history capacity is exhausted. Existing authorized results can still be opened. Waiting for expiry or refreshing source grants does not reclaim capacity; repeated retries will not fix this. Host storage maintenance is required, and automatic history deletion is not enabled."
                : message,
        );
        if (
          failure instanceof ApiError &&
          ([401, 403, 404, 410].includes(failure.status) || /proposal-stale/.test(failure.message))
        ) {
          setCompilationId(null);
          setReviewId(null);
          setChoices({});
          pendingSave.current = null;
          setContextDecisions({});
          setSourceChoices({});
          sources.refresh();
          library.refresh();
        }
      }
    } finally {
      if (version === generation.current) setBusy(false);
    }
  }
  async function compile(generateAnother = false) {
    if (
      !canReview ||
      busy ||
      activeCompileRequest.current ||
      !(mayStartCompile || (generateAnother && status?.state === "succeeded")) ||
      requestStatus.isValidating
    )
      return;
    const body = sourceIntentCompileBody.safeParse({ scenario, intakeId: intakeId.trim(),
      ...(reviewMode === "host-checks" ? { bindingKind: "host-checks" as const } : {}),
    });
    if (!body.success) {
      setError("Enter the exact saved intake ID.");
      return;
    }
    reset();
    setReviewMode(reviewMode);
    const version = generation.current;
    const requestId = `source-request:${crypto.randomUUID()}`;
    activeCompileRequest.current = requestId;
    setCompilingRequestId(requestId);
    try {
      await perform(async () => {
        const result = await apiCall(api.sourceIntentCompile, {
          query,
          body: { ...body.data, requestId },
        });
        if (version === generation.current) {
          setCompilationId(result.compilationId);
          library.refresh();
          setNotice("Compilation saved. Review every clause and exclusion below.");
        }
      });
    } finally {
      if (activeCompileRequest.current === requestId) {
        activeCompileRequest.current = null;
        setCompilingRequestId(null);
      }
      void requestStatus.mutate().catch(() => {});
    }
  }
  async function createIntake() {
    if (!canReview || sources.error || staleSourceSelection || !canCreateIntake) return;
    const parsed = sourceIntentIntakeBody.safeParse({
      scenario,
      sources: selectedSources.map((row) => ({
        ...row.source,
        expectedGrantRevision: row.grantRevision,
      })),
    });
    if (!parsed.success) {
      setError(
        "Select 2–100 current sources with content access, including a workflow template and an observed occurrence.",
      );
      return;
    }
    reset();
    setReviewMode(reviewMode);
    const version = generation.current;
    await perform(async () => {
      const result = await apiCall(api.sourceIntentIntake, { query, body: parsed.data });
      if (!result.intakeId)
        throw new Error("No persisted intake was returned; compilation is unavailable.");
      if (version === generation.current) {
        setIntakeId(result.intakeId);
        setSourceChoices({});
        library.refresh();
        setNotice(
          "Authorized intake saved and selected. Compile it to propose checks; no contract has been accepted.",
        );
      }
    });
  }
  async function cancelCompile() {
    const requestId = activeCompileRequest.current ?? (requestRunning ? status?.requestId : null);
    if (!canReview || !requestId || cancelling) return;
    const version = generation.current;
    setCancelling(true);
    try {
      const result = await apiCall(api.sourceIntentCompileCancel, {
        query,
        body: { scenario, requestId },
      });
      if (version === generation.current)
        setNotice(
          result.state === "cancellation_requested"
            ? "Cancellation recorded. A cancelled request cannot save a result. The model may still be draining; this is not proof that computation stopped."
            : "No matching active compilation was found. Refresh request status; cancellation is not confirmed.",
        );
      await requestStatus.mutate();
    } catch (failure) {
      if (version === generation.current)
        setNotice(
          `Cancellation was not confirmed: ${failure instanceof Error ? failure.message : String(failure)}`,
        );
    } finally {
      setCancelling(false);
    }
  }
  async function save() {
    if (!compiled || locked) return;
    const body = sourceReviewDraft({
      mode: reviewMode,
      scenario,
      compilationId: compiled.compilationId,
      expectedCompilationDigest: compiled.compilationDigest,
      expectedReviewDigest: saved?.reviewDigest ?? null,
      selections: Object.values(choices),
      contextDecisions: Object.entries(contextDecisions).map(([clauseId, disposition]) => ({
        clauseId,
        disposition,
      })),
    });
    const fingerprint = JSON.stringify(body);
    if (pendingSave.current?.fingerprint !== fingerprint)
      pendingSave.current = {
        fingerprint,
        body: { ...body, idempotencyKey: `review-ui:${crypto.randomUUID()}` },
      };
    const parsed = sourceIntentReviewBody.safeParse(pendingSave.current.body);
    if (!parsed.success) {
      setError(
        `Check measurement fields: ${parsed.error.issues.map((issue) => `${issue.path.join(".")}: ${issue.message}`).join("; ")}`,
      );
      return;
    }
    const version = generation.current;
    await perform(async () => {
      const result = await apiCall(api.sourceIntentReview, { query, body: parsed.data });
      if (version === generation.current) {
        setKnownSavedMode({
          reviewDigest: result.reviewDigest,
          mode: parsed.data.bindingKind === "host-checks" ? "host-checks" : "measurements",
        });
        setReviewId(result.reviewId);
        library.refresh();
        setNotice(
          "Review revision saved, not accepted. Reopen it from Saved intakes or by its ID.",
        );
        await compilation.mutate();
      }
    });
  }
  async function acceptReview() {
    if (!saved || locked || saved.gaps.length > 0 || !savedReviewStillSelected) return;
    const workItemId = acceptWorkItemId.trim();
    const version = acceptVersion.trim();
    const expectedWorkerActorId = acceptWorkerActorId.trim();
    if (!workItemId || !version || !expectedWorkerActorId) {
      setError("Enter a Universal Work ID, Contract version, and expected worker actor.");
      return;
    }
    const request = prepareSourceReviewAcceptance(
      pendingAcceptance.current,
      { tenantId, actorId },
      {
        scenario,
        reviewId: saved.reviewId,
        expectedReviewDigest: saved.reviewDigest,
        expectedCompilationDigest: saved.compilationDigest,
        workItemId,
        version,
        expectedWorkerActorId,
        deploymentMode: "REVIEW_GATED",
      },
    );
    pendingAcceptance.current = request;
    const currentGeneration = generation.current;
    await perform(async () => {
      const accepted = await apiCall(universalVerification.acceptSourceReview, {
        body: request.body,
      });
      const success = bindSourceReviewAcceptanceSuccess(request, accepted);
      if (currentGeneration === generation.current) {
        setAcceptedSourceReview(success);
        setNotice(
          `Contract accepted through server-owned source review: ${success.contractDigest}`,
        );
        library.refresh();
      }
    });
  }
  function edit(id: string, update: (choice: Choice) => Choice) {
    setChoices((old) => ({ ...old, [id]: update(old[id]!) }));
    setNotice(null);
    setAcceptedSourceReview(null);
  }
  function reopen(kind: "compilation" | "review", id: string) {
    reset();
    setLookupKind(kind);
    setLookupId(id);
    if (kind === "review") {
      setReviewId(id);
      if (id === reviewId) void review.mutate();
    } else {
      setCompilationId(id);
      if (id === compilationId) void compilation.mutate();
    }
  }
  return (
    <div className="grid min-w-0 grid-cols-1 gap-4">
      <p className="break-all type-subtext text-muted-foreground">
        Authenticated actor: {actorId}. Source access is rechecked on every request.
      </p>
      {!canReview && (
        <p role="status">Checking current mutation access, or this account cannot save reviews.</p>
      )}
      <label className="grid gap-1 type-body">
        Intake category
        <select
          className={fieldClass}
          value={scenario}
          disabled={busy}
          onChange={(event) => {
            reset();
            setIntakeId("");
            setLookupId("");
            setScenario(event.target.value as SourceIntentScenario);
          }}
        >
          {sourceIntentCompileBody.shape.scenario.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>
      <section aria-label="Create source intake" className="grid min-w-0 grid-cols-1 gap-2 rounded border p-3">
        <h3 className="font-medium">Create source intake</h3>
        <p className="type-subtext text-muted-foreground">
          Choose sources already granted to your account. This supported intake requires a
          content-authorized workflow template and a verification-authorized observed occurrence.
          Observation is context, not requirement authority. Selecting sources does not attach a
          repository or authorize execution.
        </p>
        {sources.error ? (
          <p role="alert">
            Current source grants could not be loaded. Refresh before selecting sources.
          </p>
        ) : sources.isLoading ? (
          <p role="status">Loading current source grants…</p>
        ) : sources.rows.length === 0 ? (
          <p role="status">
            No granted sources in this category. A workspace owner must provision authorized sources
            through the existing grant API; this screen cannot create grants.
          </p>
        ) : (
          sources.rows.map((row) => {
            const selectable =
              !row.source.revoked &&
              row.source.text !== null &&
              ["content", "verification"].includes(row.source.permissionTier);
            const selected = sourceChoices[row.source.id];
            const changed = selected && selected.grantRevision !== row.grantRevision;
            return (
              <div
                key={row.source.id}
                data-source-grant={row.source.id}
                className="grid min-w-0 grid-cols-1 gap-1 rounded border p-2 type-body"
              >
                <label className="flex gap-2">
                  <input
                    type="checkbox"
                    checked={Boolean(selected)}
                    disabled={busy || !canReview || !selectable}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setSourceChoices((previous) => {
                        const next = { ...previous };
                        if (checked) next[row.source.id] = row;
                        else delete next[row.source.id];
                        return next;
                      });
                    }}
                  />
                  <span className="break-all">
                    {row.source.id} · {row.source.kind}
                  </span>
                </label>
                <p>
                  {row.source.revoked
                    ? "Revoked — cannot select"
                    : `${row.source.permissionTier} permission`}
                </p>
                <p className="break-all">Source reference: {row.source.span}</p>
                {changed && (
                  <p role="alert">
                    This grant changed after selection. Deselect and reselect it before saving.
                  </p>
                )}
                {row.source.text !== null && (
                  <details>
                    <summary>Read source content</summary>
                    <p className="whitespace-pre-wrap break-words">{row.source.text}</p>
                  </details>
                )}
              </div>
            );
          })
        )}
        <p className="type-subtext">
          {selectedSources.length} sources selected. Grant revisions and permissions are rechecked
          when saving.
        </p>
        <Button
          type="button"
          variant="outline"
          disabled={busy || sources.isLoading || sources.isLoadingMore}
          onClick={() => {
            setSourceChoices({});
            sources.refresh();
          }}
        >
          Refresh source grants
        </Button>
        {!sources.error && sources.hasMore && (
          <Button type="button" disabled={busy || sources.isLoadingMore} onClick={sources.loadMore}>
            Load more sources
          </Button>
        )}
        <Button
          type="button"
          disabled={
            busy ||
            !canReview ||
            Boolean(sources.error) ||
            staleSourceSelection ||
            !canCreateIntake ||
            selectedSources.length > 100
          }
          onClick={() => void createIntake()}
        >
          Save selected source intake
        </Button>
      </section>
      <section aria-label="Saved intakes" className="grid gap-2 rounded border p-3">
        <h3 className="font-medium">Saved intakes</h3>
        <p className="type-subtext text-muted-foreground">
          Your intakes in this category, newest first. These are navigation records, not a current
          source-access or acceptance decision. Opening a result rechecks access and expiry.
        </p>
        {library.error ? (
          <p role="alert">Saved intakes could not be loaded. Refresh to retry.</p>
        ) : library.isLoading ? (
          <p role="status">Loading saved intakes…</p>
        ) : library.rows.length === 0 ? (
          <p role="status">
            No saved intakes in this category. An authorized intake must be created from current
            source grants first.
          </p>
        ) : (
          library.rows.map((item) => (
            <div
              key={item.intakeId}
              className="grid gap-2 rounded border p-2 type-body"
              data-source-intake={item.intakeId}
            >
              <p className="break-all">{item.intakeId}</p>
              <p>
                Created {item.createdAt} · {item.sourceCount ?? "Unknown"} sources
              </p>
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => {
                  reset();
                  setIntakeId(item.intakeId);
                  setNotice(
                    "Intake selected. Checking its durable request status before compilation or recovery.",
                  );
                }}
              >
                Choose intake
              </Button>
              {item.compilation && (
                <>
                  <p>
                    Latest compilation: {item.compilation.state}
                    {item.compilation.reviewRevision
                      ? ` · review revision ${item.compilation.reviewRevision}`
                      : " · no saved review"}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={
                      busy ||
                      item.compilation.state !== "saved" ||
                      Date.parse(item.compilation.expiresAt) <= now
                    }
                    onClick={() =>
                      reopen(
                        item.compilation!.reviewId ? "review" : "compilation",
                        item.compilation!.reviewId ?? item.compilation!.compilationId,
                      )
                    }
                  >
                    {item.compilation.reviewId ? "Open latest review" : "Open latest compilation"}
                  </Button>
                </>
              )}
            </div>
          ))
        )}
        <Button
          type="button"
          variant="outline"
          disabled={busy || library.isLoading || library.isLoadingMore}
          onClick={library.refresh}
        >
          Refresh saved intakes
        </Button>
        {!library.error && library.hasMore && (
          <Button type="button" disabled={busy || library.isLoadingMore} onClick={library.loadMore}>
            {library.isLoadingMore ? "Loading…" : "Load more intakes"}
          </Button>
        )}
      </section>
      <form
        className="grid gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          void compile();
        }}
      >
          <label className="grid gap-1 type-body">
            Review mode
            <select
              className={fieldClass}
              disabled={busy || !canReview || Boolean(compilingRequestId)}
              value={reviewMode}
              onChange={(event) => {
                setReviewMode(event.target.value as ReviewMode);
                pendingAcceptance.current = null;
                setAcceptedSourceReview(null);
                setNotice(null);
              }}
            >
              <option value="measurements">Choose numeric measurements</option>
              <option value="host-checks">Review host-provided checks</option>
            </select>
          </label>
        <label className="grid gap-1 type-body">
          Saved intake ID
          <input
            className={fieldClass}
            value={intakeId}
            onChange={(event) => {
              reset();
              setIntakeId(event.target.value);
            }}
            disabled={busy}
            maxLength={200}
          />
        </label>
        <Button
          type="submit"
          disabled={busy || !canReview || !mayStartCompile || requestStatus.isValidating}
        >
          Compile saved intake
        </Button>
        <p className="type-subtext text-muted-foreground">
          Use the intake saved above or choose an existing intake. Grant creation and repository
          attachment are not available on this screen.
        </p>
      </form>
      {intakeId.trim() && (
        <section
          aria-label="Compilation request recovery"
          className="grid gap-2 rounded border p-3"
          data-source-compile-recovery
        >
          <h3 className="font-medium">Compilation request recovery</h3>
          <p className="type-subtext">
            After reloading this screen, choose the same saved intake to recover its server-recorded
            request. No source text or recovery token is stored in browser storage.
          </p>
          {requestStatus.error ? (
            <p role="alert">
              Request status could not be verified: {requestStatus.error.message}. Refresh before
              starting work.
            </p>
          ) : !status ? (
            <p role="status">Checking recorded request…</p>
          ) : (
            <>
              <p role="status" data-source-compile-state={status.state}>
                Recorded request: {status.state}
              </p>
              {status.requestId && <p className="break-all type-subtext">Request: {status.requestId}</p>}
              {status.state === "none" && (
                <p>No compilation request is recorded for this intake. You can start one above.</p>
              )}
              {status.state === "running" && (
                <p>
                  A compilation lease is active until {status.leaseUntil}. Wait, refresh, or cancel
                  this request. A lease is not proof of model progress.
                </p>
              )}
              {status.state === "succeeded" && status.compilationId && (
                <Button
                  type="button"
                  disabled={busy}
                  onClick={() => reopen("compilation", status.compilationId!)}
                >
                  Open recovered compilation
                </Button>
              )}
              {status.state === "succeeded" && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy || !canReview || requestStatus.isValidating}
                  onClick={() => void compile(true)}
                >
                  Generate another compilation
                </Button>
              )}
              {status.state === "cancelled" && (
                <p>
                  This request was cancelled; it cannot publish a new result. Starting again creates
                  a new request.
                </p>
              )}
              {status.state === "interrupted" && (
                <p>
                  The worker lease expired without a saved result. No inference was automatically
                  retried. You may start a new request; the old request remains recorded.
                </p>
              )}
              {status.state === "failed" && (
                <p>
                  This request failed. Check the local model and host availability, then start a new
                  request. The failed request remains recorded.
                </p>
              )}
              {status.state === "expired" && (
                <p>
                  The saved compilation expired. Start a new request against the current authorized
                  intake.
                </p>
              )}
              {status.state === "stale" && (
                <p>
                  Source authority or revisions changed. Refresh source grants and save a fresh
                  intake; this intake cannot be recompiled.
                </p>
              )}
            </>
          )}
          <Button
            type="button"
            variant="outline"
            disabled={requestStatus.isValidating || statusIntakeId !== intakeId.trim()}
            onClick={() => void requestStatus.mutate().catch(() => {})}
          >
            Refresh request status
          </Button>
        </section>
      )}
      <form
        className="grid gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          reopen(lookupKind, lookupId.trim());
        }}
      >
        <label className="grid gap-1 type-body">
          Reopen saved result
          <select
            className={fieldClass}
            value={lookupKind}
            disabled={busy}
            onChange={(event) => setLookupKind(event.target.value as typeof lookupKind)}
          >
            <option value="compilation">Compilation</option>
            <option value="review">Review revision</option>
          </select>
        </label>
        <label className="grid gap-1 type-body">
          Result ID
          <input
            className={fieldClass}
            value={lookupId}
            onChange={(event) => setLookupId(event.target.value)}
            disabled={busy}
            maxLength={200}
          />
        </label>
        <Button type="submit" disabled={busy || !lookupId.trim()}>
          Load saved result
        </Button>
      </form>
      {busy && (
        <p role="status">
          Working… Compilation can take up to 90 seconds. Leaving this screen does not guarantee
          cancellation.
        </p>
      )}
      {(error || readError) && (
        <p role="alert" className="break-words text-destructive">
          {error ?? readError?.message} Refresh source grants and reselect after a grant conflict.
          Reload the current review after a review conflict; recompile after expiry or source
          changes.
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      {expired && (
        <p role="alert">This compilation expired. Compile the current authorized intake again.</p>
      )}
      {saved && !saved.current && (
        <div role="alert">
          Historical review; edits are disabled.{" "}
          <Button type="button" disabled={busy} onClick={() => setReviewId(saved.currentReviewId)}>
            Load current revision
          </Button>
        </div>
      )}
      {compiled && !expired && (
        <>
          <div className="min-w-0 break-all type-subtext">
            <p>Compilation: {compiled.compilationId}</p>
            <p>Digest: {compiled.compilationDigest}</p>
            <p>Model: {compiled.model}</p>
            <p>Expires: {compiled.expiresAt}</p>
          </div>
          {compiled.modelIdentity ? (
            <div className="min-w-0 break-all type-subtext">
              <p>Runtime-reported checkpoint: {compiled.modelIdentity.expectedDigest}</p>
              <p>
                Checked before and after compilation. This is not an attestation of executed
                weights.
              </p>
            </div>
          ) : (
            <p className="type-subtext">
              Historical compilation: no runtime checkpoint identity was recorded.
            </p>
          )}
          <h3 className="font-medium">Sources and exclusions</h3>
          {compiled.sources.map((source) => (
            <div key={source.sourceId} className="rounded border p-3 type-body">
              <p className="break-all">
                {source.sourceId} · {source.kind}
              </p>
              <p className="break-words">Source reference: {source.span}</p>
              <p>
                {source.exclusionReason
                  ? `Excluded: ${source.exclusionReason}`
                  : "Included under the recorded source grant"}
              </p>
            </div>
          ))}
          <h3 className="font-medium">Clauses and measurement choices</h3>
          {reviewMode === "host-checks" && current && renderCoverage && (
            <React.Fragment key={`${scenario}:${compiled.compilationId}:${compiled.compilationDigest}`}>
              {renderCoverage({ scenario, compilationId: compiled.compilationId,
                expectedCompilationDigest: compiled.compilationDigest })}
            </React.Fragment>
          )}
          {reviewMode === "host-checks" && (
            <p role="status" className="type-body">
              Save to request the host's checks for this exact compilation and your context
              decisions. The server rechecks current authority and source coverage. Missing
              authority or uncovered requirements remain blockers; review the returned checks and
              gaps before acceptance.
            </p>
          )}
          {compiled.clauses.map((clause) => {
            const predicate = compiled.predicates.find((item) => item.id === clause.id);
            const choice = choices[clause.id];
            return (
              <div
                key={clause.id}
                className="grid min-w-0 gap-2 rounded border p-3"
                data-source-clause={clause.id}
              >
                <blockquote className="whitespace-pre-wrap break-words type-body">
                  {clause.sourceSpan}
                </blockquote>
                <p className="break-all type-subtext">
                  Source: {clause.sourceId}; offsets {clause.sourceStart}–{clause.sourceEnd}.{" "}
                  {clause.kind ?? "Unclassified"}: {clause.reason ?? "Review required"}
                </p>
                {predicate && (
                  <p className="break-all font-mono type-subtext">
                    Check: {predicate.expr} · Falsifier: {predicate.falsifier}
                  </p>
                )}
                {clause.kind === "context" && (
                  <p className="type-body">
                    The model excluded this as context. This is not human approval to omit a
                    requirement.
                  </p>
                )}
                {clause.kind === "context" && clause.reason === null && (
                  <label className="grid gap-1 type-body">
                    Review context exclusion
                    <select
                      className={fieldClass}
                      disabled={locked}
                      value={contextDecisions[clause.id] ?? ""}
                      onChange={(event) => {
                        const disposition = event.target.value as "" | "context" | "requirement";
                        setContextDecisions((old) => {
                          const next = { ...old };
                          if (disposition) next[clause.id] = disposition;
                          else delete next[clause.id];
                          return next;
                        });
                        setAcceptedSourceReview(null);
                      }}
                    >
                      <option value="">Not reviewed — remains a gap</option>
                      <option value="context">I confirm this is background context</option>
                      <option value="requirement">This is a requirement — needs correction</option>
                    </select>
                    {contextDecisions[clause.id] === "requirement" && (
                      <span role="status">
                        Saving records your disagreement; it does not generate a replacement check.
                        Correct the authorized source or compilation before acceptance.
                      </span>
                    )}
                  </label>
                )}
                {reviewMode === "measurements" && clause.kind === "numeric" && predicate ? (
                  <>
                    <label className="flex gap-2 type-body">
                      <input
                        type="checkbox"
                        checked={Boolean(choice)}
                        disabled={locked}
                        onChange={(event) => {
                          setAcceptedSourceReview(null);
                          if (event.target.checked)
                            setChoices((old) => ({
                              ...old,
                              [clause.id]: {
                                predicateId: clause.id,
                                scope: {
                                  repositoryId: "",
                                  filePath: "",
                                  pointer: "",
                                  operation: "array_length",
                                  unit: "rows",
                                },
                                environmentDigest: "",
                                maximumAgeSeconds: 600,
                              },
                            }));
                          else
                            setChoices((old) => {
                              const next = { ...old };
                              delete next[clause.id];
                              return next;
                            });
                        }}
                      />
                      Specify a measurement (unselected clauses remain gaps)
                    </label>
                    {choice && (
                      <fieldset disabled={locked} className="grid min-w-0 gap-2">
                        <legend className="type-body">Unadmitted resource choices</legend>
                        {(["repositoryId", "filePath", "pointer", "unit"] as const).map((key) => (
                          <label key={key} className="grid gap-1 type-body">
                            {
                              {
                                repositoryId: "Repository ID",
                                filePath: "Relative JSON file",
                                pointer: "JSON pointer (empty means document root)",
                                unit: "Unit label",
                              }[key]
                            }
                            <input
                              className={fieldClass}
                              value={choice.scope[key]}
                              onChange={(event) =>
                                edit(clause.id, (value) => ({
                                  ...value,
                                  scope: { ...value.scope, [key]: event.target.value },
                                }))
                              }
                            />
                          </label>
                        ))}
                        <label className="grid gap-1 type-body">
                          Measurement operation
                          <select
                            className={fieldClass}
                            value={choice.scope.operation}
                            onChange={(event) =>
                              edit(clause.id, (value) => ({
                                ...value,
                                scope: {
                                  ...value.scope,
                                  operation: event.target.value as Choice["scope"]["operation"],
                                },
                              }))
                            }
                          >
                            <option value="array_length">JSON array length</option>
                            <option value="number">Recorded JSON number</option>
                          </select>
                        </label>
                        <label className="grid gap-1 type-body">
                          Environment scope digest (not yet authorized)
                          <input
                            className={fieldClass}
                            value={choice.environmentDigest}
                            onChange={(event) =>
                              edit(clause.id, (value) => ({
                                ...value,
                                environmentDigest: event.target.value,
                              }))
                            }
                          />
                        </label>
                        <label className="grid gap-1 type-body">
                          Maximum evidence age (seconds)
                          <input
                            type="number"
                            min={1}
                            max={86400}
                            step={1}
                            className={fieldClass}
                            value={choice.maximumAgeSeconds}
                            onChange={(event) =>
                              edit(clause.id, (value) => ({
                                ...value,
                                maximumAgeSeconds: Number(event.target.value),
                              }))
                            }
                          />
                        </label>
                        <p className="type-subtext text-muted-foreground">
                          Array length does not prove row quality. A recorded number does not prove
                          real latency or an external business fact. No unit conversion occurs.
                        </p>
                      </fieldset>
                    )}
                  </>
                ) : (
                  <p className="type-body">
                    {predicate
                      ? "This check needs a supported evaluator binding before acceptance."
                      : "No adjudicated executable check for this clause."}
                  </p>
                )}
              </div>
            );
          })}
          {compiled.rejected.map((item, index) => (
            <p role="note" key={index} className="break-words type-body">
              Rejected: {item.draft} — {item.reason}
            </p>
          ))}
          <Button type="button" disabled={locked} onClick={() => void save()}>
            Save review revision
          </Button>
          {saved && (
            <div className="min-w-0 break-all type-body" data-saved-source-review>
              <p>
                Review: {saved.reviewId} · revision {saved.revision}
              </p>
              <p>Digest: {saved.reviewDigest}</p>
              <p>
                {saved.status}; scope authorization: {saved.scopeAuthorization}.{" "}
                {acceptedSavedReview
                  ? "Contract acceptance recorded for this review."
                  : "Contract acceptance is not confirmed for this review."}
              </p>
              {saved.checks.map((check) => (
                <p key={check.id}>
                  {check.statement}
                  {check.kind === "exit_code" ? (
                    <>
                      {" "}
                      · Command: <code>{check.command}</code>
                    </>
                  ) : check.scope && check.comparator && check.bound !== undefined ? (
                    <>
                      : {check.comparator} {check.bound} {check.scope.unit}
                    </>
                  ) : check.kind ? (
                    <> · {check.kind}</>
                  ) : null}
                </p>
              ))}
              {saved.gaps.map((gap, index) => (
                <p key={index}>
                  Gap {gap.clauseId}: {gap.reason}
                </p>
              ))}
              {(saved.contextDecisions ?? []).map((decision) => (
                <p key={decision.clauseId}>
                  Saved exclusion decision {decision.clauseId}:{" "}
                  {decision.disposition === "context"
                    ? "confirmed background context"
                    : "disputed; requirement needs correction"}
                </p>
              ))}
              <fieldset
                disabled={
                  locked || !saved.current || saved.gaps.length > 0 || !savedReviewStillSelected
                }
                className="mt-3 grid gap-2 rounded border p-3"
              >
                <legend className="font-medium">Accept as Universal Contract</legend>
                <p className="type-subtext text-muted-foreground">
                  Acceptance rechecks the saved review, current source authority, authenticated
                  workspace membership, and the selected Universal Work before signing.
                </p>
                <label className="grid gap-1">
                  Universal Work ID
                  <input
                    className={fieldClass}
                    value={acceptWorkItemId}
                    onChange={(event) => {
                      setAcceptWorkItemId(event.target.value);
                      setAcceptedSourceReview(null);
                    }}
                    maxLength={200}
                  />
                </label>
                <label className="grid gap-1">
                  Contract version
                  <input
                    className={fieldClass}
                    value={acceptVersion}
                    onChange={(event) => {
                      setAcceptVersion(event.target.value);
                      setAcceptedSourceReview(null);
                    }}
                    maxLength={100}
                  />
                </label>
                <label className="grid gap-1">
                  Expected worker actor
                  <input
                    className={fieldClass}
                    value={acceptWorkerActorId}
                    onChange={(event) => {
                      setAcceptWorkerActorId(event.target.value);
                      setAcceptedSourceReview(null);
                    }}
                    maxLength={200}
                  />
                </label>
                <Button
                  type="button"
                  disabled={
                    locked ||
                    !saved.current ||
                    saved.gaps.length > 0 ||
                    !savedReviewStillSelected ||
                    !acceptWorkItemId.trim() ||
                    !acceptVersion.trim() ||
                    !acceptWorkerActorId.trim()
                  }
                  onClick={() => void acceptReview()}
                >
                  Accept reviewed contract
                </Button>
                {saved.gaps.length > 0 && (
                  <p role="status">Resolve all review gaps before acceptance.</p>
                )}
                {!savedReviewStillSelected && (
                  <p role="status">
                    Save this review revision before acceptance; local choices differ from the saved
                    source review.
                  </p>
                )}
                {acceptedSavedReview && (
                  <p role="status">
                    Accepted contract {acceptedSavedReview.contractId} from review{" "}
                    {acceptedSavedReview.reviewId}; digest {acceptedSavedReview.contractDigest}.
                  </p>
                )}
              </fieldset>
            </div>
          )}
        </>
      )}
      <p className="type-body text-muted-foreground">
        Contract acceptance requires a current saved review, a Universal Work item, and an
        independently registered worker actor. Repository and Environment proof are still shown
        separately in the verification loop.
      </p>
      {(compilingRequestId || requestRunning) && (
        <Button
          type="button"
          variant="outline"
          disabled={cancelling || !canReview}
          onClick={() => void cancelCompile()}
        >
          {cancelling ? "Requesting cancellation…" : "Cancel compilation"}
        </Button>
      )}
      {(compilationId || reviewId) && (
        <Button type="button" variant="outline" disabled={busy} onClick={reset}>
          Clear review from view
        </Button>
      )}
    </div>
  );
}
